import React from 'react';
import { formatTimestamp } from '../utils/date-utils';
import type { RequisitionAction, RequisitionEvent } from './requisitionsApi';
import { instance } from '../instance';
import EmptyState from '../components/ui/EmptyState';

const SENTENCE: Record<RequisitionAction, string> = {
  created: 'started the request',
  submitted: 'sent it for approval',
  withdrawn: 'changed it, which took it back to draft',
  approved: 'approved it',
  denied: 'denied it',
  cancelled: 'cancelled it',
  bought: 'bought it',
  receipt_linked: 'linked a receipt',
  receipt_unlinked: 'unlinked a receipt',
  closed: 'decided not to buy it',
  arrived: 'marked it arrived',
  shipment_added: 'added a tracking number',
  shipment_arrived: 'marked a package arrived',
  shipment_removed: 'removed a tracking number',
  line_inventory_changed: 'changed which items go into stock',
  line_received: 'received an item into stock',
};

function sentence(e: RequisitionEvent): string {
  const who = e.actor?.name || (e.channel === 'system' ? instance.displayName : 'Someone');
  let what = SENTENCE[e.action] || e.action.replace(/_/g, ' ');
  // No metadata at all is the older event shape, which only ever meant "to follow".
  if (e.action === 'bought' && (!e.metadata || e.metadata.receipt_to_follow === true)) what += ', receipt to follow';
  return `${who} ${what}${e.channel === 'slack' ? ' from Slack' : ''}`;
}

/** The words someone left with the step: a reason, or where a package is. */
function note(e: RequisitionEvent): string | null {
  if (e.reason) return e.reason;
  const n = e.metadata?.note;
  return typeof n === 'string' && n.trim() ? n : null;
}

const RequisitionTimeline: React.FC<{ events: RequisitionEvent[] }> = ({ events }) => {
  if (!events.length) {
    return <EmptyState variant="line">Nothing has happened yet.</EmptyState>;
  }
  const ordered = [...events].sort((a, b) => a.created_at.localeCompare(b.created_at));
  return (
    <ol className="space-y-3">
      {ordered.map((e, i) => {
        const words = note(e);
        return (
          <li key={`${e.created_at}-${i}`} className="relative pl-5">
            <span
              aria-hidden="true"
              className={`absolute left-0 top-1.5 h-2 w-2 rounded-full ${
                i === ordered.length - 1 ? 'bg-primary' : 'bg-surface-container-highest'
              }`}
            />
            {i < ordered.length - 1 && (
              <span aria-hidden="true" className="absolute left-[3px] top-4 bottom-[-0.75rem] w-0.5 bg-surface-container-high" />
            )}
            <div className="text-sm text-on-surface">
              {sentence(e).charAt(0).toUpperCase() + sentence(e).slice(1)}
            </div>
            <div className="text-xs tabular-nums text-on-surface-variant">
              {formatTimestamp(e.created_at, 'MMM D, h:mm A')}
            </div>
            {words && (
              <p className="mt-1 whitespace-pre-line break-words rounded-md bg-surface-container px-3 py-2 text-sm text-on-surface">
                {words}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default RequisitionTimeline;
